/**
 * SOLID Review
 * S — Strong. One concern: back into a game — the room re-joined as the record's player, then its live round.
 * O — Strong. What a restore seats is the Domain's decision.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two operations: a stored game restored, a seat's live round read.
 * D — Strong. The session's deps, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IEntityClientResume } from '../../domain/ports/schemas/ClientSession';
import type { IPayloadRoomRead } from '../../domain/ports/schemas/TableMoves';
import type { ICommandPlanRestore, ICommandRestoreJoined, ICommandRoundRestored, IPayloadSeat } from '../../domain/ports/schemas/SessionMoves';
import type { IConfigDepSession } from './IConfigDepSession';

/**
 * A SEAT RESTORED (FE2's restore and restoreRound; a collaborator since frontend-application-oo P3) — shared by the stored
 * game's entry, the code's rejoin and late join, and the cold rejoin. Every read that fails reads as "nothing": a failed
 * restore join is a game that is over, a failed round read a table between rounds.
 */
export class SeatRestore {
  constructor(private readonly deps: IConfigDepSession) {}

  /** Back into a stored game: re-join its room as the record's player, then its live round. */
  async restore(ctx: IContextRequest, record: IEntityClientResume): Promise<void> {
    const { decisions, step, rooms } = this.deps;
    const restoring: ICommandPlanRestore = { record };
    const plan = await step.run(ctx, (input) => decisions.planRestore(ctx, input, restoring));
    if (!plan.success) return;
    const joined = await rooms.joinRoom(ctx, plan.value);
    const answer: ICommandRestoreJoined = { record, room: joined.success ? joined.value : null };
    const seated = await step.run(ctx, (input) => decisions.restoreJoined(ctx, input, answer));
    if (seated.success && seated.value.read !== null) await this.restoreRound(ctx, seated.value.read);
  }

  /** The live round after a mid-game seat. */
  async restoreRound(ctx: IContextRequest, request: IPayloadSeat): Promise<void> {
    const read: IPayloadRoomRead = { roomId: request.roomId };
    const round = await this.deps.rounds.getActiveRoundForRestore(ctx, read);
    const restored: ICommandRoundRestored = { request, activeRound: round.success ? round.value : null };
    await this.deps.step.run(ctx, (input) => this.deps.decisions.roundRestored(ctx, input, restored));
  }
}
